import { useMemo, type ReactNode } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AlertCircle } from "lucide-react";
import { cn, formatCurrency } from "@/lib/utils";
import {
  QuoteResponse,
  QuoteLine,
  HydratedQuoteLine,
  QuoteItemLine,
  HydratedQuoteItemLine,
  WEIGHT_UNIT_ABBREVIATION,
  WeightUnit,
  calculateHydratedQuoteTotals,
  convertQuoteAmountToBase,
  isMultiCurrency,
  type QuoteCurrencyContext,
  type QuoteTotalsLine,
} from "@forgepoint/shared";
import Decimal from "decimal.js";

interface QuoteTotalsFooterProps {
  quote: QuoteResponse;
  isDirty: boolean;
  lines?: QuoteLine[];
  hydratedLines?: HydratedQuoteLine[];
  showTotalWeight?: boolean;
  /** Optional currency/exchange-rate control rendered atop the totals. */
  currencyControl?: ReactNode;
  /**
   * Live currency context from the editor (unsaved currency/rate changes).
   * Falls back to the quote's persisted values when omitted.
   */
  currencyContext?: QuoteCurrencyContext;
}

enum TotalsHiddenReason {
  SAVE = "save",
  QUANTITY_BREAKS = "quantityBreaks",
}

interface CalculatedTotals {
  canCalculate: boolean;
  hiddenReason: TotalsHiddenReason | null;
  subtotal: string | null;
  subtotalWithDiscounts: string | null;
  total: string | null;
}

const hasManualOverride = (
  quantityBreak: QuoteItemLine["quantityBreaks"][number],
): boolean =>
  quantityBreak.unitPriceOverride !== undefined &&
  quantityBreak.unitPriceOverride !== "";

/**
 * Checks if any item line has multiple quantity breaks.
 * When quantity breaks exist, we cannot calculate accurate totals client-side.
 */
const hasQuantityBreaks = (lines: QuoteLine[]): boolean => {
  return lines.some(
    (line) => line.type === "ITEM" && line.quantityBreaks.length > 1,
  );
};

/**
 * Checks if all quantity breaks have hydrated data available and quantities haven't changed.
 * Returns false if any break is missing hydrated data (e.g. new unsaved lines) or if
 * any quantity has changed without a price override (requires recalculation).
 */
const hasCompleteHydratedData = (
  lines: QuoteLine[],
  hydratedLines: HydratedQuoteLine[],
): boolean => {
  for (const line of lines) {
    if (line.type !== "ITEM") {
      continue;
    }

    const hydratedLine = hydratedLines.find(
      (hl): hl is HydratedQuoteItemLine =>
        hl.type === "ITEM" && hl.id === line.id,
    );

    for (let idx = 0; idx < line.quantityBreaks.length; idx++) {
      const qb = line.quantityBreaks[idx];
      const hydratedBreak = hydratedLine?.quantityBreaks?.[idx];
      const hasOverride = hasManualOverride(qb);

      if (!hasOverride && !hydratedBreak?.unitPrice) {
        return false;
      }

      const quantityChanged = qb.quantity !== hydratedBreak?.quantity;
      if (!hasOverride && quantityChanged) {
        return false;
      }
    }
  }

  return true;
};

/**
 * Merges a live editor item line's override and quantity onto its hydrated
 * unit price for totalling (first quantity break only). The override is
 * denominated in the quote's display currency, so the base `amount` derives
 * from its base equivalent at the real currency context while the quantity
 * break keeps the raw override for the display-currency totals path.
 */
const toTotalsItemLine = (
  line: QuoteItemLine,
  hydratedLine: HydratedQuoteItemLine | undefined,
  realCurrency: QuoteCurrencyContext,
): QuoteTotalsLine => {
  const qb = line.quantityBreaks[0];
  const hydratedBreak = hydratedLine?.quantityBreaks?.[0];
  const hasOverride = hasManualOverride(qb);
  const baseUnitPrice = hasOverride
    ? convertQuoteAmountToBase(qb.unitPriceOverride ?? "0", realCurrency)
    : (hydratedBreak?.unitPrice ?? null);
  const quantity = qb.quantity || "0";
  return {
    type: "ITEM",
    amount:
      baseUnitPrice === null
        ? null
        : new Decimal(baseUnitPrice).times(new Decimal(quantity)).toString(),
    quantityBreaks: [
      {
        quantity,
        unitPrice: hasOverride ? null : (hydratedBreak?.unitPrice ?? null),
        unitPriceOverride: hasOverride ? (qb.unitPriceOverride ?? null) : null,
      },
    ],
  };
};

/**
 * Builds totals lines from the live editor state when it has unsaved changes
 * or manual overrides, mirroring what the backend would hydrate.
 */
const toClientTotalsLines = (
  lines: QuoteLine[],
  hydratedLines: HydratedQuoteLine[],
  realCurrency: QuoteCurrencyContext,
): QuoteTotalsLine[] =>
  lines.map((line): QuoteTotalsLine => {
    if (line.type === "ITEM") {
      const hydratedLine = hydratedLines.find(
        (hl): hl is HydratedQuoteItemLine =>
          hl.type === "ITEM" && hl.id === line.id,
      );
      return toTotalsItemLine(line, hydratedLine, realCurrency);
    }
    if (line.type === "DISCOUNT") {
      return { type: "DISCOUNT", discount: line.discount };
    }
    return { type: line.type };
  });

/**
 * Re-denominates AMOUNT discounts into the base currency at the real currency
 * context. Stored discount amounts live in the quote's display currency, so a
 * snapshot-less (base) totals context cannot derive them itself.
 */
const withBaseAmountDiscounts = (
  totalsLines: readonly QuoteTotalsLine[],
  realCurrency: QuoteCurrencyContext,
): QuoteTotalsLine[] =>
  totalsLines.map((line) =>
    line.type === "DISCOUNT" && line.discount.type === "AMOUNT"
      ? {
          type: "DISCOUNT",
          discount: {
            type: "AMOUNT",
            amount: convertQuoteAmountToBase(
              line.discount.amount,
              realCurrency,
            ),
          },
        }
      : line,
  );

/**
 * Builds the displayable quote totals for a given currency context, handling the
 * dirty / quantity-break / incomplete-data states. Pass a context with no
 * snapshot to get base-currency totals, or the quote's context for converted
 * totals. `realCurrency` is always the quote's actual context: document-currency
 * values (overrides, AMOUNT discounts) are derived to base with it when the
 * totals context itself cannot convert.
 * @param quote - the quote whose persisted lines back the server-side totals
 * @param lines - the editor's current lines, if available
 * @param hydratedLines - hydrated line data, if available
 * @param isDirty - whether the editor has unsaved changes
 * @param currency - the currency context controlling conversion
 * @param realCurrency - the quote's actual currency context (with snapshot)
 * @returns the calculated totals and whether they can be shown
 */
const buildTotals = (
  quote: QuoteResponse,
  lines: QuoteLine[] | undefined,
  hydratedLines: HydratedQuoteLine[] | undefined,
  isDirty: boolean,
  currency: QuoteCurrencyContext,
  realCurrency: QuoteCurrencyContext,
): CalculatedTotals => {
  const totalsFor = (totalsLines: readonly QuoteTotalsLine[]) =>
    calculateHydratedQuoteTotals(
      isMultiCurrency(currency)
        ? totalsLines
        : withBaseAmountDiscounts(totalsLines, realCurrency),
      currency,
    );

  // Server-side totals re-derived from the persisted lines so multi-currency
  // quotes display the per-line converted sum (identical to quote.subtotal
  // for base-currency quotes).
  const serverTotals = totalsFor(quote.lines ?? []);

  if (!lines || !hydratedLines) {
    return {
      canCalculate: !isDirty,
      hiddenReason: isDirty ? TotalsHiddenReason.SAVE : null,
      ...serverTotals,
    };
  }

  if (hasQuantityBreaks(lines)) {
    return {
      canCalculate: false,
      hiddenReason: TotalsHiddenReason.QUANTITY_BREAKS,
      subtotal: null,
      subtotalWithDiscounts: null,
      total: null,
    };
  }

  if (!hasCompleteHydratedData(lines, hydratedLines)) {
    return {
      canCalculate: false,
      hiddenReason: TotalsHiddenReason.SAVE,
      subtotal: null,
      subtotalWithDiscounts: null,
      total: null,
    };
  }

  const hasAnyOverride = lines.some(
    (line) =>
      line.type === "ITEM" &&
      line.quantityBreaks.some((qb) => hasManualOverride(qb)),
  );

  if (!hasAnyOverride && !isDirty) {
    return { canCalculate: true, hiddenReason: null, ...serverTotals };
  }

  const clientTotals = totalsFor(
    toClientTotalsLines(lines, hydratedLines, realCurrency),
  );
  return { canCalculate: true, hiddenReason: null, ...clientTotals };
};

export function QuoteTotalsFooter({
  quote,
  isDirty,
  lines,
  hydratedLines,
  showTotalWeight,
  currencyControl,
  currencyContext,
}: QuoteTotalsFooterProps) {
  const currency: QuoteCurrencyContext = currencyContext ?? {
    quoteCurrency: quote.quoteCurrency,
    baseCurrency: quote.baseCurrency,
    snapshot: quote.snapshot,
  };
  const multiCurrency = isMultiCurrency(currency);

  // Base-currency totals (primary display); a snapshot-less context disables
  // conversion, while the real context still derives document-currency values
  // (overrides, AMOUNT discounts) to base. Converted totals back the on-hover
  // quote-currency amount. Rounding applies to the presentation currency only,
  // so the base side of a multi-currency quote stays raw.
  const baseTotals = useMemo<CalculatedTotals>(
    () =>
      buildTotals(
        quote,
        lines,
        hydratedLines,
        isDirty,
        {
          ...currency,
          snapshot: null,
          quotePriceRounding: multiCurrency
            ? null
            : currency.quotePriceRounding,
        },
        currency,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      lines,
      hydratedLines,
      quote.lines,
      currency.quoteCurrency,
      currency.snapshot,
      currency.baseCurrency,
      currency.quotePriceRounding,
      isDirty,
    ],
  );

  const convertedTotals = useMemo<CalculatedTotals>(
    () => buildTotals(quote, lines, hydratedLines, isDirty, currency, currency),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      lines,
      hydratedLines,
      quote.lines,
      currency.quoteCurrency,
      currency.snapshot,
      currency.baseCurrency,
      currency.quotePriceRounding,
      isDirty,
    ],
  );

  const totalWeightsByUnit = useMemo<Record<WeightUnit, string>>(() => {
    const sourceLines = hydratedLines ?? quote.lines ?? [];
    const itemLines = sourceLines.filter(
      (l): l is HydratedQuoteItemLine => l.type === "ITEM",
    );
    if (itemLines.some((l) => l.quantityBreaks.length > 1)) {
      return {} as Record<WeightUnit, string>;
    }
    const grouped: Partial<Record<WeightUnit, Decimal>> = {};
    for (const line of itemLines) {
      const firstBreak = line.quantityBreaks[0];
      const breakWeight = firstBreak?.totalWeight;
      const unit = firstBreak?.totalWeightUnit ?? line.item?.weightUnit;
      if (!breakWeight || !unit) {
        continue;
      }
      grouped[unit] = (grouped[unit] ?? new Decimal(0)).add(
        new Decimal(breakWeight),
      );
    }
    const result: Record<string, string> = {};
    for (const [unit, weight] of Object.entries(grouped)) {
      result[unit] = weight.toFixed(2);
    }
    return result as Record<WeightUnit, string>;
  }, [hydratedLines, quote.lines]);

  const hasWeights = Object.keys(totalWeightsByUnit).length > 0;

  const renderValue = (
    baseValue: string | null,
    convertedValue: string | null,
  ) => {
    if (!baseTotals.canCalculate) {
      const tooltipMessage =
        baseTotals.hiddenReason === TotalsHiddenReason.QUANTITY_BREAKS
          ? "Quotes with line items with multiple quantity options do not support total prices"
          : "Save your updates to recalculate prices";

      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <span className="flex items-center gap-1 text-muted-foreground">
                <AlertCircle className="h-4 w-4" />
                Hidden
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{tooltipMessage}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    if (!baseValue) {
      return "-";
    }

    const base = formatCurrency(baseValue, currency.baseCurrency ?? undefined);
    if (!multiCurrency || convertedValue === null) {
      return base;
    }

    // Show the original base-currency total pointing to the converted
    // quote-currency amount: base → converted.
    return (
      <span className="inline-flex items-center gap-1.5">
        <span>{base}</span>
        <span className="text-muted-foreground">&rarr;</span>
        <span>
          {formatCurrency(convertedValue, currency.quoteCurrency ?? undefined)}
        </span>
      </span>
    );
  };

  return (
    <Card>
      <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
        <div className="space-y-2">
          {currencyControl && (
            <>
              {currencyControl}
              <Separator />
            </>
          )}
          <div className="flex justify-between gap-2">
            <span className="text-xs sm:text-sm text-muted-foreground">
              Subtotal:
            </span>
            <span
              className={cn(
                "font-medium tabular-nums text-sm sm:text-base",
                !baseTotals.canCalculate && "text-muted-foreground",
              )}
            >
              {renderValue(baseTotals.subtotal, convertedTotals.subtotal)}
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-xs sm:text-sm text-muted-foreground">
              Subtotal with Discounts:
            </span>
            <span
              className={cn(
                "font-medium tabular-nums text-sm sm:text-base",
                !baseTotals.canCalculate && "text-muted-foreground",
              )}
            >
              {renderValue(
                baseTotals.subtotalWithDiscounts,
                convertedTotals.subtotalWithDiscounts,
              )}
            </span>
          </div>
          <Separator />
          <div className="flex justify-between gap-2 text-base sm:text-lg font-semibold">
            <span>Total:</span>
            <span
              className={cn(
                "tabular-nums",
                !baseTotals.canCalculate && "text-muted-foreground",
              )}
            >
              {renderValue(baseTotals.total, convertedTotals.total)}
            </span>
          </div>
          {showTotalWeight && hasWeights && baseTotals.canCalculate && (
            <>
              <Separator />
              {Object.entries(totalWeightsByUnit).map(([unit, weight]) => (
                <div key={unit} className="flex justify-between gap-2">
                  <span className="text-xs sm:text-sm text-muted-foreground">
                    Total Weight:
                  </span>
                  <span className="font-medium tabular-nums text-sm sm:text-base">
                    {weight}{" "}
                    {WEIGHT_UNIT_ABBREVIATION[unit as WeightUnit] ?? unit}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
